import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import API_BASE_URL from '../../config';
import axios from 'axios';
import { ChevronDown, ArrowRight } from 'lucide-react';
import '../../Styles/Admin/assessment/AssLogin.css';

// Must exactly match CANONICAL_DEPARTMENTS in assessmentReportRoutes.js —
// keeping one list is what guarantees new submissions never mismatch
// against the report page's department filter again.
const departments = [
  "Human Resources (HR)",
  "Finance & Accounts",
  "Sales & Marketing",
  "Information Technology (IT)",
  "Operations & Administration",
  "IT Support",
  "Customer Support / Customer Service",
  "Business Development",
  "Procurement / Purchase",
  "Legal & Compliance",
  "Training & Development",
  "Maintenance, Quality & Production",
  "Research & Development (R&D)",
  "Audit & Risk Management",
  "Facilities Management",
  "Medical Coding",
  "Field Services",
  "Warehouse & Logistics",
  "Service Delivery",
  "Others"
];
const genders = ['Male', 'Female'];

// NOTE: exact experience-band labels were not specified — using a common
// set of ranges. Edit this array if you want different bands.
const experienceOptions = [
  "Fresher (0-1 years)",
  "1-3 years",
  "3-5 years",
  "5-10 years",
  "10+ years"
];

// Pulls the slug out of a stored company_url like "/assessment/vanakkamhrd/login"
// so it can be compared directly against the URL param, instead of guessing
// from the company's display name.
const extractSlugFromCompanyUrl = (companyUrl) => {
  if (!companyUrl) return '';
  const match = companyUrl.match(/\/assessment\/([^/]+)\/login/i);
  return match ? match[1].toLowerCase() : '';
};

/* ---------- Custom dropdown ----------
   Replaces the native <select> for Experience / Department / Gender.
   The option list is positioned relative to its own trigger (not the
   viewport), is width-capped to the field itself, and its height is
   capped to a percentage of the viewport with internal scrolling — so on
   mobile it always opens fully inside the visible screen and never
   overlays past the edges. */
function FieldDropdown({ label, required, value, onChange, options, placeholder, half }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handleOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
    };
  }, [open]);

  useEffect(() => {
    // Keep the opened list inside the visible viewport when possible.
    if (open && wrapRef.current) {
      wrapRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [open]);

  return (
    <div className={`ass-log-form-group${half ? ' ass-log-form-group-half' : ''}`}>
      <label className="ass-log-label">
        {label}{required && <span className="ass-log-required">*</span>}
      </label>
      <div className="ass-log-dropdown" ref={wrapRef}>
        <button
          type="button"
          className="ass-log-dropdown-trigger"
          onClick={() => setOpen(o => !o)}
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          <span className={`ass-log-dropdown-value${value ? '' : ' placeholder'}`}>
            {value || placeholder}
          </span>
          <ChevronDown className={`ass-log-dropdown-arrow${open ? ' open' : ''}`} size={18} />
        </button>

        {open && (
          <div className="ass-log-dropdown-list" role="listbox">
            {options.map(opt => (
              <button
                type="button"
                key={opt}
                role="option"
                aria-selected={value === opt}
                className={`ass-log-dropdown-item${value === opt ? ' selected' : ''}`}
                onClick={() => {
                  onChange(opt);
                  setOpen(false);
                }}
              >
                {opt}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function AssessmentLogin() {
  const { companySlug } = useParams();
  const [company, setCompany] = useState('');
  const [companies, setCompanies] = useState([]);
  const [companyLocked, setCompanyLocked] = useState(false);
  const [lockedCompanyName, setLockedCompanyName] = useState('');
  const [slugLookupDone, setSlugLookupDone] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [experience, setExperience] = useState('');
  const [department, setDepartment] = useState('');
  const [gender, setGender] = useState('');
  const [showInstructions, setShowInstructions] = useState(false);
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [alertMsg, setAlertMsg] = useState('');

  useEffect(() => {
    // Reset per-slug state whenever the route changes
    setSlugLookupDone(false);
    setCompanyLocked(false);
    setLockedCompanyName('');
    setAlertMsg('');
    setButtonDisabled(false);

    // Fetch BOTH lists together:
    // - activeRes: active-only companies (server-filtered), used for the dropdown AND to
    //   determine whether a slug-matched company is active.
    // - fullRes: full list (active + inactive), used only to resolve the slug.
    Promise.all([
      axios.get(`${API_BASE_URL}/api/assessment/companies?active=1`).catch(() => ({ data: [] })),
      companySlug
        ? axios.get(`${API_BASE_URL}/api/assessment/companies`).catch(() => null)
        : Promise.resolve(null)
    ]).then(([activeRes, fullRes]) => {
      const activeList = Array.isArray(activeRes.data) ? activeRes.data : [];
      setCompanies(activeList);

      if (!companySlug) {
        setSlugLookupDone(true);
        return;
      }

      if (!fullRes || !Array.isArray(fullRes.data)) {
        setCompanyLocked(true);
        setLockedCompanyName('');
        setAlertMsg('Unable to load this company right now. Please try again later.');
        setButtonDisabled(true);
        setSlugLookupDone(true);
        return;
      }

      const slugNorm = (companySlug || '').toLowerCase();

      // Primary match: compare against the company's own company_url field
      // (the exact path set in Admin → Companies), not a guess from the name.
      let match = fullRes.data.find(c => extractSlugFromCompanyUrl(c.company_url) === slugNorm);

      // Fallback for older companies that don't have company_url set: fall
      // back to the old "slug is contained in the name" heuristic.
      if (!match) {
        const normalize = str => (str || '').toLowerCase().replace(/[^a-z0-9]/g, '');
        const slugNormAlnum = normalize(companySlug);
        match = fullRes.data.find(c => normalize(c.name).includes(slugNormAlnum));
      }

      if (match) {
        setCompany(match.id);
        setCompanyLocked(true);
        setLockedCompanyName(match.name);

        // Active status is determined by presence in the active-only list (by id).
        const isActive = activeList.some(c => c.id === match.id);
        if (!isActive) {
          setAlertMsg("This company's assessment is not currently active.");
          setButtonDisabled(true);
        }
      } else {
        // No match found for this slug — still lock the box, never fall back to the dropdown.
        setCompanyLocked(true);
        setLockedCompanyName('');
        setAlertMsg('');
        setButtonDisabled(true);
      }

      setSlugLookupDone(true);
    });
  }, [companySlug]);

  // Check for existing user and answers when email or name changes
  useEffect(() => {
    const checkUserStatus = async () => {
      if (!email || !name) {
        return;
      }
      try {
        const res = await axios.get(`${API_BASE_URL}/api/assessment-answers?employee_email=${email}`);
        const data = res.data;
        const userExists = data.employee && data.employee.employee_email === email;
        const hasAnswers = data.answers && data.answers.length > 0;
        if (userExists && hasAnswers) {
          setButtonDisabled(true);
          setAlertMsg('Our records indicate that you have already completed this survey. ');
        }
      } catch (err) {
        // no-op — don't clear an existing inactive/invalid-company alert
      }
    };
    checkUserStatus();
  }, [email, name]);

  const navigate = useNavigate();

const handleSubmit = async (e) => {
  e.preventDefault();
  if (buttonDisabled) return;
  if (!experience || !department || !gender) {
    setAlertMsg('Please select experience, department and gender before submitting.');
    return;
  }

  // Puthu email login pannuna, previous stale timer/answers clear pannum
  const resetStaleProgressIfNeeded = (newEmail) => {
    const previousEmail = localStorage.getItem('employee_email');
    if (previousEmail !== newEmail) {
      localStorage.removeItem('assessment_start_time');
      localStorage.removeItem('assessment_answers');
      localStorage.removeItem('assessment_current_category_idx');
      localStorage.removeItem('assessment_max_reached_idx');
    }
  };

  try {
    await axios.post(`${API_BASE_URL}/api/employees`, {
      company_id: company,
      name,
      email,
      experience,
      department,
      gender
    });
    resetStaleProgressIfNeeded(email);
    localStorage.setItem('employee_email', email);
    setShowInstructions(true);
  } catch (err) {
    if (err.response && err.response.data && err.response.data.message === 'Employee already exists') {
      resetStaleProgressIfNeeded(email);
      localStorage.setItem('employee_email', email);
      setShowInstructions(true);
    } else {
      alert("Error saving your details. Please try again.");
    }
  }
};
  const handleStart = () => {
    navigate('/assessment/questions');
  };

  if (showInstructions) {
    return (
      <div className="ass-log-page">
        <img src="/New_logo.jpg" alt="Outstanding Workplaces" className="ass-log-logo" />

        <div className="ass-log-welcome-card">
          <div className="ass-log-welcome-icon-wrap">
            <img src="/Ass_log_img.png" alt="Assessment" className="ass-log-welcome-icon-img" />
          </div>

          <h2 className="ass-log-welcome-heading">Welcome to the Assessment!</h2>
          <p className="ass-log-welcome-sub">
            You have <span className="ass-log-highlight">30 minutes</span> to complete{' '}
            <span className="ass-log-highlight">40 questions</span>
          </p>

          <hr className="ass-log-divider" />

          <ul className="ass-log-welcome-list">
            <li>
              <img src="/Ass_tick_icon.png" alt="" className="ass-log-welcome-check-icon" />
              <span>Since there are 5 Options for each question.</span>
            </li>
            <li>
              <img src="/Ass_tick_icon.png" alt="" className="ass-log-welcome-check-icon" />
              <span>Manage your time wisely to answer all questions</span>
            </li>
            <li>
              <img src="/Ass_tick_icon.png" alt="" className="ass-log-welcome-check-icon" />
              <span>Since there are no negative marks, attempt all questions.</span>
            </li>
          </ul>

          <p className="ass-log-welcome-tagline">Our Culture, Our Strength!</p>

          <button className="ass-log-start-btn" onClick={handleStart}>
            Ready to Start
            <ArrowRight size={20} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="ass-log-page">
      <img src="/New_logo.jpg" alt="Outstanding Workplaces" className="ass-log-logo" />

      <h1 className="ass-log-heading">Employee Assessment Survey</h1>
      <p className="ass-log-subheading">Please enter your details below to begin the assessment.</p>

      <div className="ass-log-card">
        <form onSubmit={handleSubmit}>
          {/* Row 1: Full Name & Email ID */}
          <div className="ass-log-form-row">
            <div className="ass-log-form-group">
              <label className="ass-log-label">
                Full Name<span className="ass-log-required">*</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                required
                placeholder="Enter Your Name"
                className="ass-log-input"
              />
            </div>
            <div className="ass-log-form-group">
              <label className="ass-log-label">
                Email ID<span className="ass-log-required">*</span>
              </label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                placeholder="Enter Email ID"
                className="ass-log-input"
              />
            </div>
          </div>

          {/* Row 2: Experience & Department — custom dropdowns */}
          <div className="ass-log-form-row">
            <FieldDropdown
              label="Experience"
              required
              value={experience}
              onChange={setExperience}
              options={experienceOptions}
              placeholder="-Select Experience-"
            />
            <FieldDropdown
              label="Department"
              required
              value={department}
              onChange={setDepartment}
              options={departments}
              placeholder="-Select Department-"
            />
          </div>

          {/* Row 3: Gender - single box, left side only */}
          <div className="ass-log-form-row">
            <FieldDropdown
              label="Gender"
              required
              half
              value={gender}
              onChange={setGender}
              options={genders}
              placeholder="-Select Gender-"
            />
          </div>

          <div className="ass-log-actions">
            {alertMsg && <div className="ass-log-alert">{alertMsg}</div>}
            <button
              type="submit"
              className="ass-log-submit-btn"
              disabled={buttonDisabled}
            >
              LOGIN
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AssessmentLogin;